/**
 * TutorialPlayer — Picture-in-Picture style modal for tutorial videos.
 *
 * Shows a "Coming Soon" placeholder when the Remotion MP4 is not yet
 * available, or plays the rendered video when `videoSrc` is provided.
 *
 * Usage:
 *   <TutorialPlayer tutorial={tutorialObj} onClose={() => setPlaying(null)} />
 */

import React, { useEffect } from "react";

const SERIES_COLORS = {
  "Onboarding":             "#545cd8",
  "Strategy":               "#3ac47d",
  "Rules":                  "#f7b924",
  "Monitoring & Execution": "#30b1ff",
  "Settings":               "#d92550",
};

export default function TutorialPlayer({ tutorial, onClose }) {
  /* close on Escape */
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  if (!tutorial) return null;

  const accent = SERIES_COLORS[tutorial.series] || "#545cd8";

  return (
    <>
      {/* backdrop */}
      <div
        onClick={onClose}
        style={{
          position: "fixed", inset: 0, zIndex: 1200,
          background: "rgba(0,0,0,0.55)", backdropFilter: "blur(3px)",
        }}
      />

      {/* player panel — PiP-style right-side drawer */}
      <div
        style={{
          position: "fixed", top: 60, right: 0, bottom: 0, zIndex: 1201,
          width: Math.min(560, window.innerWidth - 32),
          background: "#fff",
          boxShadow: "-4px 0 32px rgba(0,0,0,0.18)",
          display: "flex", flexDirection: "column",
          overflow: "hidden",
          animation: "slideInRight 0.25s ease",
        }}
      >
        <style>{`
          @keyframes slideInRight {
            from { transform: translateX(60px); opacity: 0; }
            to   { transform: translateX(0);    opacity: 1; }
          }
        `}</style>

        {/* header */}
        <div style={{
          padding: "14px 20px",
          borderBottom: "1px solid #eee",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          flexShrink: 0,
          background: accent,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{
              background: "rgba(255,255,255,0.25)", color: "#fff",
              borderRadius: 6, padding: "2px 8px", fontSize: 11, fontWeight: 700,
            }}>
              {tutorial.series}
            </span>
            <span style={{ color: "#fff", fontSize: 13, fontWeight: 600 }}>
              #{tutorial.id} — {tutorial.title}
            </span>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "rgba(255,255,255,0.2)", border: "none",
              borderRadius: 6, color: "#fff", fontSize: 18, lineHeight: 1,
              width: 30, height: 30, cursor: "pointer", display: "flex",
              alignItems: "center", justifyContent: "center",
            }}
          >
            ×
          </button>
        </div>

        {/* video area */}
        <div style={{
          background: "#0d0d1a",
          aspectRatio: "16/9",
          display: "flex", alignItems: "center", justifyContent: "center",
          flexDirection: "column", gap: 16,
          flexShrink: 0,
          position: "relative",
          overflow: "hidden",
        }}>
          {tutorial.videoSrc ? (
            <video
              src={tutorial.videoSrc}
              controls
              autoPlay
              style={{ width: "100%", height: "100%", objectFit: "contain" }}
            />
          ) : (
            <>
              {/* animated placeholder */}
              <div style={{
                width: 72, height: 72, borderRadius: "50%",
                background: `${accent}33`,
                border: `3px solid ${accent}88`,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 32,
              }}>
                🎬
              </div>
              <div style={{ color: "#fff", fontSize: 16, fontWeight: 600 }}>
                Coming Soon
              </div>
              <div style={{ color: "#888", fontSize: 12, textAlign: "center", maxWidth: 280 }}>
                This tutorial is being produced. Check back after the next release.
              </div>
              <div style={{
                position: "absolute", top: 12, right: 14,
                background: "rgba(255,255,255,0.12)",
                color: "#ccc", fontSize: 11, padding: "3px 10px",
                borderRadius: 20, fontWeight: 500,
              }}>
                {tutorial.duration}
              </div>
            </>
          )}
        </div>

        {/* meta + description */}
        <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
            <span style={{
              background: `${accent}18`, color: accent,
              border: `1px solid ${accent}44`,
              borderRadius: 20, padding: "2px 10px",
              fontSize: 11, fontWeight: 600,
            }}>
              {tutorial.duration}
            </span>
            <span style={{
              background: tutorial.priority === "P0" ? "#fff3cd" : "#f0f0f0",
              color: tutorial.priority === "P0" ? "#856404" : "#666",
              border: `1px solid ${tutorial.priority === "P0" ? "#ffc107" : "#ddd"}`,
              borderRadius: 20, padding: "2px 10px",
              fontSize: 11, fontWeight: 600,
            }}>
              {tutorial.priority === "P0" ? "⭐ Essential" : tutorial.priority === "P1" ? "Recommended" : "Advanced"}
            </span>
          </div>

          <h3 style={{ margin: "0 0 8px", fontSize: 17, color: "#222", fontWeight: 700 }}>
            {tutorial.title}
          </h3>
          <p style={{ margin: "0 0 16px", fontSize: 13, color: "#555", lineHeight: 1.6 }}>
            {tutorial.desc}
          </p>

          {tutorial.topics && tutorial.topics.length > 0 && (
            <>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#aaa", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 8 }}>
                What you'll learn
              </div>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {tutorial.topics.map((t, i) => (
                  <li key={i} style={{ fontSize: 13, color: "#555", marginBottom: 4, lineHeight: 1.5 }}>
                    {t}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </div>
    </>
  );
}
